import { useState } from 'react';
import { Size, t } from '@bms2/shared';
import { DatePicker, Dropdown, TextInput } from '../../components/controls';
import { FormFrame } from '../../components/forms';
import { LEADS_PATH } from './LeadStageCards';
import { post } from './api';
import { useEmploymentSearch } from './DealModals';

/**
 * Add Absence (piece 20; decision 0173): a customer's or supplier's employee who is away, entered by hand.
 * 🔨 The person is picked as an employment, because the absence is the employment's; the write confirms
 * it on the spot, since the person typing it is the one who knows. Keyed by its host so a reopened window
 * never shows the last draft.
 */
export function AbsenceModal({ open, apiBaseUrl = '', onCancel, onSaved }: { open: boolean; apiBaseUrl?: string; onCancel: () => void; onSaved: () => void }) {
  const search = useEmploymentSearch(apiBaseUrl, null);
  const [employment, setEmployment] = useState<{ id: string; label: string } | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setError(null);
    if (!employment) return setError(t('hygiene.absence.needsPerson'));
    if (!from) return setError(t('hygiene.absence.needsFrom'));
    if (to && to < from) return setError(t('hygiene.absence.endsBeforeStart'));
    const answer = await post(`${LEADS_PATH}/hygiene/absences`, { employmentId: employment.id, startDate: from, expectedReturnDate: to || null, note: reason.trim() || null }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open={open}
      title={t('hygiene.absence.title')}
      onCancel={onCancel}
      dataForm="absence"
      typed={employment !== null || from !== '' || to !== '' || reason !== ''}
      act={{ label: t('modal.save'), onAct: () => save() }}
      refusal={error}
      onDismissRefusal={() => setError(null)}
    >
      <div data-absence-field="person">
        <Dropdown size={Size.Large} label={t('hygiene.absence.person')} entityLabel={t('hygiene.absence.personEntity')} search={search} value={employment?.label ?? null} onSelect={(row) => setEmployment({ id: row.id, label: row.line1 })} onAddNew={null} required />
      </div>
      <DatePicker size={Size.Medium} label={t('hygiene.absence.fromDate')} value={from} onChange={setFrom} mode="date" name="absence-from" required />
      <DatePicker size={Size.Medium} label={t('hygiene.absence.toDate')} value={to} onChange={setTo} mode="date" name="absence-to" />
      <TextInput size={Size.Large} label={t('hygiene.absence.reason')} value={reason} onChange={setReason} name="absence-reason" />
    </FormFrame>
  );
}
